/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { ProductDimensionFull } from '@msdyn365-commerce/commerce-entities';
import { ICoreContext, ITelemetry } from '@msdyn365-commerce/core';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { getTelemetryObject, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { Dropdown, IDropdownItemProps, IDropdownOnSelectionChangeNotification } from './dropdown';
import { IErrorState,
    IProductDimensionCallbacks,
    IProductDimensionConfigureDropdownViewProps,
    IProductDimensionConfigureResources,
    IProductDimensionConfigureViewProps } from './product-dimension-interface';

let telemetryContent: ITelemetryContent;

// Quantity on change.
const onChange = async (
    notification: IDropdownOnSelectionChangeNotification,
    dimensionChanged: (newValue: number, selectedDimensionValue: string) => Promise<void>
): Promise<void> => {
    await dimensionChanged(+notification.dropdownId, notification.selectId);
};

// This method will map all product dimension to correct value and returns respective dropdown.
const mapProductDimensionFullToDropdownViewProps =
    (
        productDimensionFull: ProductDimensionFull,
        moduleName: string,
        selectDimensionFormatString: string,
        resources: IProductDimensionConfigureResources,
        getDropdownName: (dimensionType: number, resources: IProductDimensionConfigureResources) => string,
        dropdownCallback: (notification: IDropdownOnSelectionChangeNotification) => Promise<void>,
        configureErrors?: { [configureId: string]: string | undefined }
    ): IProductDimensionConfigureDropdownViewProps => {
        const dropdownName = getDropdownName(productDimensionFull.DimensionTypeValue, resources);
        const dropdownId = productDimensionFull.DimensionTypeValue.toString();
        const dropdownToggleName = selectDimensionFormatString.replace('{0}', dropdownName.toLocaleLowerCase());
        const dropdownList: IDropdownItemProps[] = productDimensionFull.DimensionValues ? productDimensionFull.DimensionValues.map<IDropdownItemProps>(dM => {
            return { value: dM.Value || '', id: dM.RecordId.toString() };
        }) : [];
        const errorMessage = configureErrors ? configureErrors[dropdownId] : '';

        return {
            containerProps: {
                className: `${moduleName}__dropdown`
            },
            labelContainerProps: {
                tag: 'label',
                className: `${moduleName}__dropdown-quantity-label`,
                htmlFor: `${moduleName}__dropown-quantity-input-${dropdownId}`
            },
            errors: errorMessage && (
                <span className='msc-alert msc-alert-noborder msc-alert-danger'>
                    <span className='msi-exclamation-triangle' aria-hidden='true' />
                    <span>
                        {errorMessage}
                    </span>
                </span>
            ),
            heading: (
                <div>
                    {dropdownName}
                </div>
            ),
            select: (
                <Dropdown
                    dropdownId={dropdownId}
                    dropdownName={dropdownName}
                    dropdownToggleName={dropdownToggleName}
                    dropdownList={dropdownList}
                    onChange={dropdownCallback}
                    telemetryContent={telemetryContent}
                />
            )
        };
    };

export const getProductConfigure = (moduleName: string, context: ICoreContext, pageName: string, telemetry: ITelemetry, selectDimensionFormatString: string,
    resources: IProductDimensionConfigureResources,
    callbacks: IProductDimensionCallbacks, product?: SimpleProduct, productDimensions?: ProductDimensionFull[],
    configureErrors?: IErrorState): IProductDimensionConfigureViewProps | undefined => {

    const
        { getDropdownName } = callbacks;

    if (!product || !productDimensions) {
        return undefined;
    }

    const onChanged = async (notification: IDropdownOnSelectionChangeNotification) => onChange(notification, callbacks.dimensionSelectedAsync);

    telemetryContent = getTelemetryObject(context.request.telemetryPageName!, pageName, telemetry);

    if (!ArrayExtensions.hasElements(productDimensions)) {
        return undefined;
    }

    const dropdowns = productDimensions.map((productDimensionFull: ProductDimensionFull) => mapProductDimensionFullToDropdownViewProps(productDimensionFull,
        moduleName, selectDimensionFormatString, resources, getDropdownName, onChanged, configureErrors?.configureErrors));

    return {
        containerProps: {
            className: `${moduleName}__configure`
        },
        dropdowns
    };
};
